/**
 * Selectable AI SMS lines + regenerate unselected (matches single-country preview UX).
 */

import { Languages, RefreshCw } from 'lucide-react';

import { Button, LoadingSpinner } from '@/components/ui';
import { cn } from '@/lib/utils';
import { PreviewTextItem } from '@/features/campaign-texts/components/PreviewTextItem';
import { SMS_TEXT_MAX_LENGTH } from '@/features/campaign-texts/smsTextLimit';

export interface AiResultTextBatchProps {
  texts: string[];
  translations?: (string | null | undefined)[];
  picksRow: boolean[] | undefined;
  disableInteractions?: boolean;
  onToggleLine: (lineIdx: number) => void;
  onSetAllInBatch: (value: boolean) => void;
  onRegenUnselected: () => void | Promise<void>;
  isRegenerating: boolean;
  showSectionTranslate?: boolean;
  sectionNeedsEnglish?: boolean;
  isTranslatingSection?: boolean;
  onTranslateSection?: () => void | Promise<void>;
}

function pickFlags(texts: string[], picksRow: boolean[] | undefined): boolean[] {
  if (!picksRow || picksRow.length !== texts.length) return texts.map(() => true);
  return picksRow;
}

export function AiResultTextBatch({
  texts,
  translations,
  picksRow,
  disableInteractions,
  onToggleLine,
  onSetAllInBatch,
  onRegenUnselected,
  isRegenerating,
  showSectionTranslate,
  sectionNeedsEnglish,
  isTranslatingSection,
  onTranslateSection,
}: AiResultTextBatchProps) {
  if (texts.length === 0) return null;
  const flags = pickFlags(texts, picksRow);
  const selectedCount = flags.filter(Boolean).length;
  const unselected = texts.length - selectedCount;
  const allOn = selectedCount === texts.length;

  return (
    <div className={cn('space-y-1', disableInteractions && 'pointer-events-none opacity-60')}>
      <div className="flex flex-wrap items-center justify-between gap-1">
        <span className="text-[10px] font-medium text-muted-foreground">
          Texts ({selectedCount}/{texts.length})
        </span>
        <div className="flex flex-wrap items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[10px]"
            disabled={disableInteractions}
            onClick={() => onSetAllInBatch(!allOn)}
          >
            {allOn ? 'Deselect all' : 'Select all'}
          </Button>
          {showSectionTranslate && sectionNeedsEnglish && onTranslateSection && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-2 text-[10px]"
              disabled={disableInteractions || isTranslatingSection}
              onClick={() => void onTranslateSection()}
            >
              {isTranslatingSection ? (
                <LoadingSpinner className="h-3 w-3" />
              ) : (
                <Languages className="h-3 w-3" />
              )}
              Translate
            </Button>
          )}
          {unselected > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 gap-1 px-2 text-[10px]"
              disabled={disableInteractions || isRegenerating}
              onClick={() => void onRegenUnselected()}
            >
              {isRegenerating ? (
                <LoadingSpinner className="h-3 w-3" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              Regen {unselected}
            </Button>
          )}
        </div>
      </div>
      <ul className="max-h-52 space-y-0.5 overflow-y-auto">
        {texts.map((t, i) => (
          <li key={i} className="list-none">
            <PreviewTextItem
              text={t}
              index={i}
              isSelected={flags[i] ?? true}
              onToggle={onToggleLine}
              lengthLimit={SMS_TEXT_MAX_LENGTH}
              lengthSourceText={t}
              compact
              subtitle={translations?.[i] ?? null}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
